import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import {
    ensureSelectedAgent,
    isCvoProductServiceName,
    isMarketplaceProductAssociated,
    resolveMarketplaceAutoSelect,
} from "../../../utils/cvoMarketplaceUtils";

/**
 * Owns every AWS CVO marketplace side effect that runs at the app-shell level, keeping App.js free
 * of marketplace-specific logic:
 *  1. Fetch/save/clear the marketplace context once on app initialization (login, or a
 *     subscription-mapping redirect for the standalone CVO product).
 *  2. Auto-select the marketplace-provider agent exactly once per context — see
 *     `resolveMarketplaceAutoSelect` in cvoMarketplaceUtils.js for the full rationale.
 *  3. Once associated and the matching active agent is selected, clear the marketplace context if
 *     a CVO already exists under that agent (setup goal met) so Home no longer shows Resume.
 */
export const useMarketplaceAppEffects = ({ isAuthenticated, isSaaSUI, selectedOrgId }) => {
    const dispatch = useDispatch();
    const location = useLocation();
    const marketplaceState = useSelector(state => state.marketplaceContext);
    const agentStatus = useSelector(state => state.tenancy?.agentStatus);
    const agents = useSelector(state => state.tenancy?.agents || []);
    const selectedAgent = useSelector(state => state.tenancy?.selectedAgent);
    const selectedAgentUserId = useSelector(state => state.tenancy?.selectedAgentUserId);

    // Fetch marketplace context once on app initialization (for marketplace users)
    // Wait for organization to be selected before fetching to ensure tenancy data is available
    useEffect(() => {
        if (isAuthenticated && isSaaSUI && selectedOrgId) {
            const searchParams = new URLSearchParams(location.search);
            const token = searchParams.get("token");
            const cloudProvider = searchParams.get("cloudProvider");
            const serviceName = searchParams.get("serviceName");

            // Marketplace context is only for the standalone CVO product (serviceName === "cvo-product").
            // - subscription-mapping redirect with cvo-product   -> SAVE (persist context for our flow)
            // - subscription-mapping redirect with a different    -> CLEAR any stale cvo-product context
            //   serviceName for that cloud provider                  (backend + local) so our flow doesn't trigger
            // - any other app load (normal login)                 -> FETCH
            const isSubscriptionMappingRedirect = location.pathname.includes("subscription-mapping") && token && cloudProvider;
            const isCvoProduct = isCvoProductServiceName(serviceName);
            if (isSubscriptionMappingRedirect && isCvoProduct) {
                dispatch({
                    type: "MARKETPLACE-CONTEXT:SAVE",
                    payload: { cloudProvider, serviceName, registrationToken: token }
                });
            } else if (isSubscriptionMappingRedirect && !isCvoProduct) {
                // Clear the stale cvo-product context for this cloud provider only if one actually
                // exists (no spurious DELETE).
                dispatch({
                    type: "MARKETPLACE-CONTEXT:CLEAR-IF-EXISTS",
                    payload: { cloudProvider }
                });
            } else {
                dispatch({
                    type: "MARKETPLACE-CONTEXT:FETCH"
                });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated, isSaaSUI, selectedOrgId, dispatch]);

    // Auto-select the marketplace provider agent exactly once per context — see
    // `resolveMarketplaceAutoSelect` in cvoMarketplaceUtils.js for the full rationale.
    const marketplaceAutoSelectContextKeyRef = useRef(null);
    useEffect(() => {
        const { nextContextKey, agentToSelect } = resolveMarketplaceAutoSelect({
            row: marketplaceState?.row,
            agentStatus,
            agents,
            resolvedContextKey: marketplaceAutoSelectContextKeyRef.current,
        });
        marketplaceAutoSelectContextKeyRef.current = nextContextKey;
        if (agentToSelect) {
            ensureSelectedAgent(dispatch, selectedAgent, agentToSelect);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [marketplaceState?.row, agentStatus]);

    // Once associated and the matching active agent is selected, clear the marketplace context if a
    // CVO already exists under that agent (setup goal met) so Home no longer shows the Resume button.
    // A ref prevents duplicate dispatches when selectedAgentUserId re-evaluates mid-flow (e.g. agent
    // polling refreshes the userId) while the async DELETE is already in flight.
    const clearIfCvoExistsDispatchedRef = useRef(false);
    useEffect(() => {
        const row = marketplaceState?.row;
        const isAssociated = isMarketplaceProductAssociated(marketplaceState?.liveState);
        const marketplaceProvider = row?.cloudProvider?.toLowerCase();
        // Reset guard whenever context row changes (new subscription mapping or context cleared).
        if (!row) {
            clearIfCvoExistsDispatchedRef.current = false;
            return;
        }
        if (!isAssociated || !selectedAgentUserId) {
            return;
        }
        // Only proceed once the matching active agent is the selected one.
        if (selectedAgent?.provider !== marketplaceProvider || selectedAgent?.status !== 'active') {
            return;
        }
        if (clearIfCvoExistsDispatchedRef.current) {
            return;
        }
        clearIfCvoExistsDispatchedRef.current = true;
        // No payload here — the middleware reads selectedAgentUserId from getState() itself
        // (it needs to be fresh at the time the async check resolves, not at dispatch time).
        dispatch({ type: "MARKETPLACE-CONTEXT:CLEAR-IF-CVO-EXISTS" });
    }, [marketplaceState?.row, marketplaceState?.liveState, selectedAgent, selectedAgentUserId, dispatch]);
};

export default useMarketplaceAppEffects;
